import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { createLinkToken, exchangeToken, getAccounts, disconnectAccount as disconnectAccountFunction, syncTransactions as syncTransactionsFunction } from '@/services/firebaseFunctions';
import { 
  PlaidItem, 
  BankAccount, 
  PlaidLinkSuccessResponse,
  PlaidLinkErrorResponse,
  CreateLinkTokenResponse,
  GetAccountsResponse,
  ExchangeTokenRequest,
  DisconnectAccountRequest
} from '@/types/plaid';

interface PlaidContextType {
  // States
  plaidItems: PlaidItem[];
  bankAccounts: BankAccount[];
  isLoading: boolean;
  error: string | null;
  
  // Functions
  initializePlaidLink: () => Promise<string>;
  handlePlaidSuccess: (response: PlaidLinkSuccessResponse) => Promise<void>;
  handlePlaidError: (error: PlaidLinkErrorResponse) => void;
  disconnectAccount: (plaidItemId: string, accountId?: string) => Promise<void>;
  refreshAccounts: (forceRefresh?: boolean) => Promise<void>;
  syncTransactions: (plaidItemId: string) => Promise<void>;
}

const PlaidContext = createContext<PlaidContextType | undefined>(undefined);

export const PlaidProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [plaidItems, setPlaidItems] = useState<PlaidItem[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize Plaid Link and get link token
  const initializePlaidLink = useCallback(async (): Promise<string> => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!currentUser?.uid) {
        throw new Error('User not authenticated');
      }

      const result = await createLinkToken();
      return (result.data as CreateLinkTokenResponse).link_token;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize Plaid Link');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.uid]);

  // Handle successful Plaid Link connection
  const handlePlaidSuccess = useCallback(async (response: PlaidLinkSuccessResponse) => {
    try {
      setIsLoading(true);
      setError(null);

      if (!currentUser?.uid) {
        throw new Error('User not authenticated');
      }

      // Exchange public token for access token
      await exchangeToken({
        public_token: response.public_token,
        metadata: response.metadata
      } as ExchangeTokenRequest);

      // Refresh accounts after successful connection
      await refreshAccounts(true); // Force refresh after new connection
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process Plaid connection');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.uid]);

  // Handle Plaid Link errors
  const handlePlaidError = useCallback((error: PlaidLinkErrorResponse) => {
    setError(`Plaid Error: ${error.error_message}`);
  }, []);

  // Disconnect a Plaid account
  const disconnectAccount = useCallback(async (plaidItemId: string, accountId?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!plaidItemId) {
        throw new Error('No Plaid item ID provided');
      }

      await disconnectAccountFunction({ plaidItemId, accountId } as DisconnectAccountRequest);

      // Refresh accounts after successful disconnect
      await refreshAccounts(true); // Force refresh after disconnect
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to disconnect account';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh accounts data with optional force refresh
  const refreshAccounts = useCallback(async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await getAccounts({ includeBalances: true, forceRefresh });
      const { plaidItems, accounts } = result.data as GetAccountsResponse;
      
      setPlaidItems(plaidItems);
      setBankAccounts(accounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh accounts');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Add the syncTransactions function implementation
  const syncTransactions = useCallback(async (plaidItemId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await syncTransactionsFunction({ plaidItemId });
      console.log('Transactions synced:', result.data);
      
      // Optionally refresh accounts after sync
      await refreshAccounts(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sync transactions';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [refreshAccounts]);

  const value = {
    plaidItems,
    bankAccounts,
    isLoading,
    error,
    initializePlaidLink,
    handlePlaidSuccess,
    handlePlaidError,
    disconnectAccount,
    refreshAccounts,
    syncTransactions,
  };

  return (
    <PlaidContext.Provider value={value}>
      {children}
    </PlaidContext.Provider>
  );
};

export const usePlaid = () => {
  const context = useContext(PlaidContext);
  if (context === undefined) {
    throw new Error('usePlaid must be used within a PlaidProvider');
  }
  return context;
}; 